<template>
  <div class="min-h-[60vh]">
    <div class="container-wide py-10 lg:py-14 max-w-5xl mx-auto">

      <!-- Header -->
      <header class="relative mb-8 rounded-2xl overflow-hidden">
        <div class="absolute inset-0 bg-hero-fade opacity-60 pointer-events-none"></div>
        <div class="relative">
          <div class="eyebrow mb-3">
            <Icon name="search" :size="12" />
            New analysis
          </div>
          <h1 class="font-display text-4xl lg:text-5xl text-ink-900 leading-tight text-balance">
            Audit any URL. <span class="italic text-brand-700">Get signal</span> in minutes.
          </h1>
          <p class="mt-3 text-ink-600 max-w-xl">
            Enter a URL, pick a depth, and hit audit. We'll run the full pipeline and redirect you to a live progress view.
          </p>
        </div>
      </header>

      <!-- Pending analysis banner -->
      <div v-if="pendingAnalysis" class="alert-warn mb-6">
        <Icon name="clock" :size="16" classes="mt-0.5 shrink-0" />
        <div class="flex-1">
          <div class="font-medium text-warn-800">You have an analysis in progress</div>
          <div class="text-xs text-warn-700 mt-0.5">Resume to see live progress, or dismiss if you abandoned it.</div>
        </div>
        <div class="flex gap-2 shrink-0">
          <button class="btn-brand btn-sm" @click="resumeAnalysis" :disabled="checkingStatus">
            <svg v-if="checkingStatus" class="animate-spin w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            Resume
          </button>
          <button class="btn-ghost btn-sm" @click="dismissAnalysis">Dismiss</button>
        </div>
      </div>

      <!-- No reports remaining banner (Starter tier) -->
      <div v-if="reportsExhausted && !isAdmin" class="relative overflow-hidden rounded-2xl mb-6 bg-gradient-to-r from-brand-600 via-brand-700 to-brand-800 text-white shadow-lg shadow-brand-500/20">
        <!-- Glow orb -->
        <div class="absolute -top-16 -right-16 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute -bottom-10 -left-10 w-32 h-32 bg-brand-400/20 rounded-full blur-2xl pointer-events-none"></div>
        <div class="relative p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div class="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
            <Icon name="zap" :size="20" classes="text-white" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-bold text-white text-base">You've used all 5 free audits</div>
            <div class="text-sm text-white/80 mt-1 leading-relaxed">Get 30 audits/month, AI Intelligence Copilot, auto-competitor discovery, and priority support.</div>
          </div>
          <button class="btn-sm rounded-xl font-semibold shrink-0 bg-white text-brand-700 hover:bg-white/90 shadow-md transition-all duration-200 hover:scale-105 active:scale-95" @click="router.push('/pricing')">
            Upgrade to Pro
          </button>
        </div>
      </div>

      <!-- Low reports warning (Starter tier, 1-2 remaining) -->
      <div v-else-if="reportsLow && !reportsExhausted && !isAdmin" class="relative overflow-hidden rounded-2xl mb-6 bg-gradient-to-r from-warn-50 via-warn-50 to-warn-100 border border-warn-200/80 shadow-sm">
        <div class="absolute -top-8 -right-8 w-24 h-24 bg-warn-400/15 rounded-full blur-2xl pointer-events-none"></div>
        <div class="relative p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div class="w-11 h-11 rounded-xl bg-warn-100 flex items-center justify-center shrink-0">
            <span class="text-xl font-extrabold text-warn-700">{{ reportsRemaining }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-bold text-warn-900">Only {{ reportsRemaining }} audit{{ reportsRemaining === 1 ? '' : 's' }} remaining this month</div>
            <div class="text-sm text-warn-700/80 mt-1 leading-relaxed">Running low — upgrade now to keep your SEO momentum going.</div>
          </div>
          <button class="btn-sm rounded-xl font-semibold shrink-0 bg-warn-600 text-white hover:bg-warn-700 shadow-md transition-all duration-200 hover:scale-105 active:scale-95" @click="router.push('/pricing')">
            See plans
          </button>
        </div>
      </div>

      <!-- No audits remaining banner (Pro/Agency, bonus-eligible tiers) -->
      <div v-if="auditsExhausted && !isAdmin" class="relative overflow-hidden rounded-2xl mb-6 bg-gradient-to-r from-brand-600 via-brand-700 to-brand-800 text-white shadow-lg shadow-brand-500/20">
        <div class="absolute -top-16 -right-16 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute -bottom-10 -left-10 w-32 h-32 bg-brand-400/20 rounded-full blur-2xl pointer-events-none"></div>
        <div class="relative p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div class="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
            <Icon name="zap" :size="20" classes="text-white" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-bold text-white text-base">You've used all your audits for this billing cycle</div>
            <div class="text-sm text-white/80 mt-1 leading-relaxed">Get {{ auditPackSize }} more audits instantly, without waiting for next month's reset.</div>
          </div>
          <button
            type="button"
            class="btn-sm rounded-xl font-semibold shrink-0 bg-white text-brand-700 hover:bg-white/90 shadow-md transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-70 disabled:hover:scale-100 disabled:cursor-wait"
            :disabled="buyingAudits"
            @click="buyAudits"
          >
            <LoadingSpinner v-if="buyingAudits" size="sm" color="var(--wl-brand-700)" track-color="rgba(15,76,129,0.2)" />
            <Icon v-else name="plus-circle" :size="14" />
            {{ buyingAudits ? 'Redirecting…' : `Buy ${auditPackSize} more audits — $${auditPackPrice}` }}
          </button>
        </div>
      </div>

      <!-- Low audits warning (Pro/Agency, <=5 remaining). Admins are
           excluded -- their own account's audit count isn't a meaningful
           real-usage signal. -->
      <div v-else-if="auditsLow && !isAdmin" class="group relative overflow-hidden rounded-2xl mb-6 bg-gradient-to-br from-brand-50 via-brand-50 to-accent-50 border border-brand-100 hover:border-brand-300 hover:shadow-hero shadow-sm transition-all duration-200">
        <div class="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-accent-300/30 blur-2xl pointer-events-none" aria-hidden="true"></div>
        <div class="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-brand-300/20 blur-2xl pointer-events-none" aria-hidden="true"></div>
        <div class="relative p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div class="w-11 h-11 rounded-xl bg-white/70 flex items-center justify-center shrink-0">
            <Icon name="alert-triangle" :size="20" classes="text-brand-700" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-bold text-ink-900">Only {{ reportsRemaining }} audit{{ reportsRemaining === 1 ? '' : 's' }} remaining this month</div>
            <div class="text-sm text-ink-600 mt-1 leading-relaxed">Running low on your {{ tierDisplayName }} plan — top up instead of waiting for the reset.</div>
          </div>
          <button
            type="button"
            class="btn-sm rounded-xl font-semibold shrink-0 bg-brand-600 text-white hover:bg-brand-700 shadow-md transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-70 disabled:hover:scale-100 disabled:cursor-wait"
            :disabled="buyingAudits"
            @click="buyAudits"
          >
            <LoadingSpinner v-if="buyingAudits" size="sm" color="#fff" track-color="rgba(255,255,255,0.3)" />
            {{ buyingAudits ? 'Redirecting…' : `Buy ${auditPackSize} more — $${auditPackPrice}` }}
          </button>
        </div>
      </div>

      <!-- Buy-audits error banner -->
      <div v-if="buyAuditsError" class="alert-danger mb-6">
        <Icon name="alert-triangle" :size="16" classes="mt-0.5 shrink-0" />
        <span>{{ buyAuditsError }}</span>
      </div>

      <!-- Main form card -->
      <form @submit.prevent="submitAnalysis" class="card p-6 lg:p-8 space-y-7">
        <!-- URL input -->
        <div>
          <div class="flex items-baseline justify-between mb-1.5">
            <label class="label !mb-0" for="url">Website URL</label>
            <Tooltip
              title="What we analyze"
              body="Any public URL. We run the full pipeline — technical, content, performance, backlinks — in parallel."
              placement="top"
            >
              <button type="button" class="text-xs text-ink-500 hover:text-ink-700 flex items-center gap-1">
                <Icon name="info" :size="12" /> What's analyzed?
              </button>
            </Tooltip>
          </div>

          <div class="flex flex-col sm:flex-row gap-2">
            <div class="input-group flex-1">
              <span class="input-group-addon"><Icon name="globe" :size="16" /></span>
              <input
                id="url"
                v-model="form.url"
                type="text"
                class="input input-lg"
                placeholder="https://yourdomain.com"
                autocomplete="url"
                required
              />
            </div>
            <button type="button" class="btn-outline btn-lg whitespace-nowrap" @click="previewWebsite" :disabled="previewLoading">
              <Icon v-if="!previewLoading" name="eye" :size="16" />
              <svg v-else class="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
              Preview
            </button>
          </div>

          <!-- URL history -->
          <div v-if="urlHistory.length > 0" class="mt-3 flex flex-wrap items-center gap-2">
            <span class="text-xs text-ink-500 mr-1">Recent:</span>
            <button
              v-for="u in urlHistory"
              :key="u"
              type="button"
              class="chip"
              @click="form.url = u"
            >{{ truncateUrl(u) }}</button>
            <button type="button" class="text-xs text-ink-500 hover:text-danger-600 ml-auto" @click="clearUrlHistory">
              Clear
            </button>
          </div>

          <!-- Preview result -->
          <Transition name="fade">
            <div v-if="previewData" class="mt-4 alert"
                 :class="previewData.accessible ? 'alert-success' : 'alert-danger'">
              <Icon :name="previewData.accessible ? 'check-circle' : 'alert-triangle'"
                    :size="16" classes="mt-0.5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="font-medium">
                  {{ previewData.accessible ? 'Domain found' : 'Domain not found' }}
                </div>
                <div class="text-xs opacity-80 mt-0.5">
                  {{ previewData.domain || form.url }}<span v-if="previewData.status"> · {{ previewData.status }}</span>
                </div>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Mode selection -->
        <div>
          <label class="label">Analysis depth</label>
          <!-- Report redesign follow-up (2026-08-20): these 3 cards used to
               claim different check-counts (30+ vs 100+) and durations
               (45s / 2-3min / 3min) for Quick/Standard/Advanced, and
               Advanced's badge implied AI Copilot access required Pro+.
               None of that is true: clean_pipeline.py runs the identical
               17-module engine for every mode -- confirmed via a full grep,
               the only real `analysis_mode` branch anywhere in the backend
               is force_rendered (Advanced forces full JS rendering). AI
               enhancement runs for every mode too (gated on whether
               DeepSeek is configured, not on mode). SEO Assistant access
               is now tier-based, not mode-based (chat_endpoints.py). Copy
               below reflects only what's actually true; the honest shared
               note underneath explains the one real distinction. -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <!-- Quick -->
            <button
              type="button"
              class="text-left p-5 rounded-xl border-2 transition-all duration-200 relative"
              :class="form.mode === 'quick'
                ? 'border-ink-900 bg-ink-50/60 shadow-soft'
                : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50/30'"
              @click="form.mode = 'quick'"
            >
              <div class="flex items-start justify-between mb-3">
                <span class="w-9 h-9 rounded-lg bg-ink-100 text-ink-700 flex items-center justify-center">
                  <Icon name="zap" :size="16" />
                </span>
                <span class="badge-neutral">{{ tierLimitLabel }}</span>
              </div>
              <div class="font-semibold text-ink-900">Quick Scan</div>
              <p class="text-xs text-ink-600 mt-1 leading-relaxed">The full audit engine. Pick this when you just want the headline score.</p>
              <p class="text-2xs text-ink-500 mt-2 italic">{{ tierLimitCaption }}</p>
            </button>

            <!-- Standard -->
            <button
              type="button"
              class="text-left p-5 rounded-xl border-2 transition-all duration-200 relative"
              :class="form.mode === 'standard'
                ? 'border-brand-500 bg-brand-50/40 shadow-card'
                : 'border-ink-200 hover:border-brand-200 hover:bg-brand-50/20'"
              @click="form.mode = 'standard'"
            >
              <div class="absolute -top-2.5 right-5 bg-ink-900 text-white text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">
                Recommended
              </div>
              <div class="flex items-start justify-between mb-3">
                <span class="w-9 h-9 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center">
                  <Icon name="bar-chart" :size="16" />
                </span>
                <span class="badge-brand">1 credit · {{ reportsDisplay }} left</span>
              </div>
              <div class="font-semibold text-ink-900">Standard</div>
              <p class="text-xs text-ink-600 mt-1 leading-relaxed">Full 100+ point audit — Core Web Vitals, backlinks, keyword research. The default for ongoing tracking.</p>
              <p class="text-2xs text-ink-500 mt-2 italic">{{ tierLimitCaption }}</p>
            </button>

            <!-- Advanced (real gate: forces full headless-browser JS
                 rendering, which costs more compute per scan -- see the
                 top-of-block comment. Not an AI-access gate; SEO Assistant
                 is available on every tier. -->
            <button
              type="button"
              class="text-left p-5 rounded-xl border-2 transition-all duration-200 relative overflow-hidden"
              :class="form.mode === 'advanced'
                ? 'border-accent-500 bg-accent-50/40 shadow-card'
                : 'border-ink-200 hover:border-accent-300 hover:bg-accent-50/20'"
              @click="selectAdvancedMode"
            >
              <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-accent-400/20 to-transparent rounded-bl-full"></div>
              <div class="flex items-start justify-between mb-3 relative">
                <span class="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                  <Icon name="sparkles" :size="16" />
                </span>
                <span v-if="!isPaidTier" class="badge-warn">
                  <Icon name="lock" :size="10" /> Pro+
                </span>
                <span v-else class="badge-accent">Full JS render</span>
              </div>
              <div class="font-semibold text-ink-900 relative">Advanced</div>
              <p class="text-xs text-ink-600 mt-1 leading-relaxed relative">Same full audit, plus forced full-page JavaScript rendering — for React/Vue/Angular and other JS-heavy sites. Uses more compute per scan.</p>
              <p class="text-2xs text-ink-500 mt-2 italic relative">
                {{ isPaidTier ? 'Included on ' + tierDisplayName : 'Requires Pro or above' }}
              </p>
            </button>
          </div>
          <p class="text-xs text-ink-500 mt-2.5 flex items-start gap-1.5">
            <Icon name="info" :size="13" class="mt-0.5 shrink-0" />
            Every scan runs the same full audit — no watered-down free version. Advanced additionally forces full JavaScript rendering, which is why it's on Pro and above.
          </p>
        </div>

        <!-- Target keywords.
             Promoted out of "Advanced options" deliberately. Search Console
             rankings, Share of Voice, SERP features and the competitor gap all
             query DataForSEO PER KEYWORD -- with none supplied there is nothing
             to query, so those sections come back empty and the keyword list
             falls back to phrases scraped off the page, most of which have no
             search volume. Collapsed behind a toggle, the single input that
             unlocks roughly half the report was one most people never opened. -->
        <div>
          <div class="flex items-baseline justify-between mb-1.5">
            <label class="label !mb-0" for="keywords">Target keywords</label>
            <button type="button" class="text-xs font-medium link inline-flex items-center gap-1" @click="suggestKeywords" :disabled="suggestLoading">
              <Icon name="sparkles" :size="12" />
              {{ suggestLoading ? 'Finding…' : 'AI suggest' }}
            </button>
          </div>
          <textarea
            id="keywords"
            v-model="form.keywords"
            class="textarea"
            rows="2"
            placeholder="seo, keyword research, site audit"
          ></textarea>
          <p class="helper">Comma or newline separated. Leave blank and we'll still audit the page — you just won't get ranking positions or competitive share for it.</p>

          <!-- Suggestions chips -->
          <div v-if="keywordSuggestions.length > 0" class="mt-3 flex flex-wrap gap-1.5">
            <button
              v-for="kw in keywordSuggestions"
              :key="kw"
              type="button"
              class="chip"
              @click="addKeyword(kw)"
            >
              <Icon name="plus" :size="10" />
              {{ kw }}
            </button>
          </div>

          <p class="mt-3 flex items-start gap-1.5 text-xs text-ink-500">
            <Icon name="info" :size="13" class="mt-0.5 shrink-0" />
            <span>
              Keywords unlock <strong class="text-ink-700">ranking positions</strong>,
              <strong class="text-ink-700">Share of Voice</strong>,
              <strong class="text-ink-700">SERP features</strong> and the
              <strong class="text-ink-700">competitor gap</strong>. Without them
              those sections have nothing to measure against.
            </span>
          </p>
        </div>

        <!-- Advanced toggle section -->
        <div>
          <button
            type="button"
            class="flex items-center gap-2 text-sm font-medium text-ink-700 hover:text-ink-900 transition-colors"
            @click="showAdvanced = !showAdvanced"
          >
            <Icon :name="showAdvanced ? 'chevron-down' : 'chevron-right'" :size="14" />
            Advanced options
            <span v-if="!showAdvanced" class="text-xs text-ink-500 font-normal">
              · competitors, region, auto-discover
            </span>
          </button>

          <Transition name="collapse">
            <div v-if="showAdvanced" class="mt-5 space-y-5 pt-5 border-t border-ink-100">
              <!-- Competitors -->
              <div>
                <label class="label" for="competitors">
                  Competitors
                  <span class="text-xs font-normal text-ink-500 ml-1">(up to 3)</span>
                </label>
                <textarea
                  id="competitors"
                  v-model="form.competitors"
                  class="textarea font-mono text-xs"
                  rows="3"
                  placeholder="domain-1.com&#10;domain-2.com"
                ></textarea>
                <p class="helper">One domain per line (without http:// or https://). We'll benchmark scores and content side-by-side.</p>
              </div>

              <!-- Region - DYNAMIC from API -->
              <div>
                <label class="label" for="region">Search region</label>
                <div class="relative">
                  <select 
                    id="region" 
                    v-model="form.region" 
                    class="select max-w-xs"
                    :disabled="regionsLoading"
                  >
                    <option v-if="regionsLoading" value="us">Loading regions...</option>
                    <optgroup v-for="(group, groupKey) in availableRegions" :key="groupKey" :label="formatGroupLabel(groupKey)">
                      <option 
                        v-for="region in group" 
                        :key="region.code" 
                        :value="region.code"
                      >
                        {{ region.name }}
                      </option>
                    </optgroup>
                  </select>
                  <div v-if="regionsLoading" class="absolute right-8 top-1/2 -translate-y-1/2">
                    <svg class="animate-spin w-4 h-4 text-ink-500" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25"/>
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
                    </svg>
                  </div>
                </div>
                <p class="helper">Used for keyword volume and SERP position data. Affects competitor gap analysis.</p>
              </div>

              <!-- NEW: Auto-discover competitors (Premium feature) -->
              <div class="pt-2">
                <div class="flex items-start justify-between">
                  <label class="flex items-start gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      v-model="form.auto_competitors" 
                      class="mt-0.5 w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      :disabled="!canUseAutoCompetitors"
                    />
                    <div>
                      <div class="flex items-center gap-2">
                        <div class="text-sm font-medium text-ink-900">Auto-discover natural competitors</div>
                        <Tooltip
                          title="Premium Feature"
                          body="Finds your top 5 competitors from Google search results and analyzes their keywords automatically."
                          placement="top"
                        >
                          <button class="text-ink-500 hover:text-ink-600">
                            <Icon name="info" size="14" />
                          </button>
                        </Tooltip>
                      </div>
                      <p class="text-xs text-ink-500 mt-0.5">
                        Automatically find top 5 competitors from Google SERP based on your keywords
                      </p>
                      <div class="flex items-center gap-2 mt-1.5">
                        <span class="text-2xs bg-warn-50 text-warn-700 px-2 py-0.5 rounded-full">
                          ⚡ {{ isPaidTier ? 'Included on ' + tierDisplayName : 'Requires Pro or above' }}
                        </span>
                      </div>
                    </div>
                  </label>
                  
                  <!-- Upgrade badge for starter users -->
                  <div v-if="!isPaidTier && !isAdmin" class="text-right">
                    <span class="badge-warn text-2xs">
                      <Icon name="lock" size="10" /> Requires Pro or above
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Submit -->
        <div class="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-5 border-t border-ink-100">
          <div class="text-xs text-ink-500 flex items-center gap-2">
            <Icon name="info" :size="14" />
            <span>Running <strong class="text-ink-800">{{ modeName }}</strong> mode with <strong class="text-ink-800">{{ getRegionName(form.region) }}</strong> search data.</span>
            <span v-if="form.auto_competitors" class="text-brand-600 ml-2">
              · Auto-competitors enabled
            </span>
          </div>
          <button type="submit" class="btn-primary btn-lg whitespace-nowrap" :disabled="loading || !form.url">
            <svg v-if="loading" class="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
            <Icon v-else name="rocket" :size="16" />
            {{ loading ? 'Starting…' : 'Start analysis' }}
          </button>
        </div>
      </form>

      <!-- Quick tips strip -->
      <div class="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div class="flex items-start gap-3 p-4 rounded-xl bg-ink-50/60 border border-ink-100">
          <Icon name="zap" :size="16" classes="text-brand-600 mt-0.5 shrink-0" />
          <div>
            <div class="font-medium text-ink-900">Live progress</div>
            <div class="text-xs text-ink-600 mt-0.5">WebSocket streaming shows every module as it runs.</div>
          </div>
        </div>
        <div class="flex items-start gap-3 p-4 rounded-xl bg-ink-50/60 border border-ink-100">
          <Icon name="globe" :size="16" classes="text-brand-600 mt-0.5 shrink-0" />
          <div>
            <div class="font-medium text-ink-900">Mobile + desktop</div>
            <div class="text-xs text-ink-600 mt-0.5">Both viewports analyzed for Core Web Vitals.</div>
          </div>
        </div>
        <div class="flex items-start gap-3 p-4 rounded-xl bg-ink-50/60 border border-ink-100">
          <Icon name="target" :size="16" classes="text-brand-600 mt-0.5 shrink-0" />
          <div>
            <div class="font-medium text-ink-900">Impact-ranked fixes</div>
            <div class="text-xs text-ink-600 mt-0.5">See what actually moves rankings first.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { usePageMeta } from '../../composables/usePageMeta.js';

// Server-injected (core/page_meta.py) and client-rendered tags are two hand-
// maintained copies of the same strings; test_meta_description_lengths asserts
// they match verbatim, so edit both together.
usePageMeta({
  title: 'Free SEO & AI Search Audit — LeadRanks',
  meta: [
    { name: 'description', content: 'Audit any site against 100+ SEO and AI-search checks in minutes. No signup for your first report — see your score, your issues, and what to fix first.' },
    { property: 'og:title', content: 'Free SEO & AI Search Audit — LeadRanks' },
    { property: 'og:description', content: 'Audit any site against 100+ SEO and AI-search checks in minutes. No signup for your first report — see your score, your issues, and what to fix first.' },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
});
import { useRouter, useRoute } from 'vue-router';
import { useCheckoutButton } from '../../composables/useCheckoutButton';
import { trackAuditStarted, trackPurchase } from '../../analytics/events.js';
import Icon from '../ui/Icon.vue';
import Tooltip from '../ui/Tooltip.vue';
import LoadingSpinner from '../ui/LoadingSpinner.vue';

const router = useRouter();
const route = useRoute();

// ========== CSRF TOKEN HELPERS ==========
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return '';
};

const getCsrfTokenFromCookie = () => {
  return getCookie('csrf_token');
};

// Delegates to the app-wide window.fetchWithCSRF (main.js) -- that one
// self-heals (fetches/refreshes a real token via /api/csrf-token) if
// the csrf_token cookie is missing or stale; this file's own prior
// implementation read the cookie directly with no such fallback (same
// bug class documented in CLAUDE.md's anti-regression #51/#58/#101).
// Kept as a same-named local wrapper so every existing call site below
// (regions/status/preview/keywords/analyze/auth-me) needed zero changes.
const fetchWithCSRF = async (url, options = {}) => {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  return window.fetchWithCSRF(url, { ...options, headers });
};

const syncCsrfTokenToMeta = () => {
  const csrfToken = getCsrfTokenFromCookie();
  if (csrfToken) {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) {
      meta.setAttribute('content', csrfToken);
    }
  }
};

// ========== REGIONS FROM API ==========
const availableRegions = ref({
  north_america: [],
  europe: [],
  asia_pacific: [],
  south_america: [],
  middle_east_africa: []
});
const regionsLoading = ref(true);

const loadRegions = async () => {
  try {
    const response = await fetchWithCSRF('/api/regions');
    const data = await response.json();
    if (data.success && data.regions) {
      availableRegions.value = data.regions;
    }
  } catch (err) {
    console.error('Failed to load regions:', err);
    availableRegions.value = {
      north_america: [{ code: 'us', name: 'United States' }],
      europe: [{ code: 'uk', name: 'United Kingdom' }],
      asia_pacific: [{ code: 'au', name: 'Australia' }],
      south_america: [],
      middle_east_africa: []
    };
  } finally {
    regionsLoading.value = false;
  }
};

const formatGroupLabel = (groupKey) => {
  const labels = {
    north_america: 'North America',
    europe: 'Europe',
    asia_pacific: 'Asia Pacific',
    south_america: 'South America',
    middle_east_africa: 'Middle East & Africa'
  };
  return labels[groupKey] || groupKey;
};

const getRegionName = (regionCode) => {
  for (const group of Object.values(availableRegions.value)) {
    const found = group.find(r => r.code === regionCode);
    if (found) return found.name;
  }
  return regionCode.toUpperCase();
};

// ========== PENDING ANALYSIS LOGIC ==========
const checkingStatus = ref(false);

const checkAnalysisStatus = async (analysisId) => {
  try {
    const response = await fetchWithCSRF(`/api/analysis/${analysisId}/status`);
    const data = await response.json();
    return data.status;
  } catch (err) {
    console.error('Failed to check analysis status:', err);
    return null;
  }
};

const resumeAnalysis = async () => {
  if (!pendingAnalysis.value) return;
  
  checkingStatus.value = true;
  
  try {
    const status = await checkAnalysisStatus(pendingAnalysis.value);
    
    if (status === 'completed') {
      dismissAnalysis();
      router.push(`/report/${pendingAnalysis.value}`);
    } else if (status === 'failed') {
      dismissAnalysis();
      toast('Previous analysis failed. You can start a new one.', 'warn');
    } else if (status === 'processing' || status === 'queued' || status === 'pending') {
      router.push(`/processing/${pendingAnalysis.value}`);
    } else {
      dismissAnalysis();
      toast('Previous analysis no longer exists. You can start a new one.', 'info');
    }
  } catch (err) {
    console.error('Error resuming analysis:', err);
    dismissAnalysis();
  } finally {
    checkingStatus.value = false;
  }
};

const dismissAnalysis = () => {
  pendingAnalysis.value = null;
  localStorage.removeItem('pending_analysis_id');
};

const verifyPendingAnalysis = async () => {
  const stored = localStorage.getItem('pending_analysis_id');
  if (stored) {
    const status = await checkAnalysisStatus(stored);
    if (status === 'completed' || status === 'failed') {
      localStorage.removeItem('pending_analysis_id');
      pendingAnalysis.value = null;
    } else if (status === 'processing' || status === 'queued' || status === 'pending') {
      pendingAnalysis.value = stored;
    } else {
      localStorage.removeItem('pending_analysis_id');
      pendingAnalysis.value = null;
    }
  }
};

// ---- MAIN FORM LOGIC ----
const loading = ref(false);
const previewLoading = ref(false);
const suggestLoading = ref(false);
const showAdvanced = ref(false);
const form = ref({
  url: '',
  mode: 'standard',
  keywords: '',
  competitors: '',
  region: 'us',
  auto_competitors: false
});

const previewData = ref(null);
const keywordSuggestions = ref([]);
const pendingAnalysis = ref(null);
const reportsRemaining = ref(0);
const totalAnalyses = ref(0);
const subscriptionTier = ref('starter');
const isAdmin = ref(false);

const urlHistory = ref([]);
const URL_HISTORY_KEY = 'seo_checker_url_history';

const modeName = computed(() => ({
  quick: 'Quick Scan', standard: 'Standard', advanced: 'Advanced',
}[form.value.mode] || 'Standard'));

// Paid tiers: all tiers except starter/free (backward-compat with legacy names)
const PAID_TIERS = ['professional', 'pro', 'agency', 'enterprise', 'growth', 'scale', 'business', 'premium'];
const isPaidTier = computed(() => PAID_TIERS.includes(subscriptionTier.value));

// Tier metadata for card context
const TIER_MONTHLY_LIMITS = {
  starter: 3, free: 3,
  professional: 30, pro: 30, growth: 30,
  agency: 60, scale: 60, business: 60,
  enterprise: 0,
};
const TIER_DISPLAY_NAMES = {
  starter: 'Starter', free: 'Starter',
  professional: 'Pro', pro: 'Pro', growth: 'Pro',
  agency: 'Agency', scale: 'Agency', business: 'Agency',
  enterprise: 'Enterprise', premium: 'Enterprise',
};
const tierMonthlyLimit = computed(() => TIER_MONTHLY_LIMITS[subscriptionTier.value] ?? 3);
const tierDisplayName = computed(() => TIER_DISPLAY_NAMES[subscriptionTier.value] ?? 'Starter');

// Starter's audit cap is a one-time, lifetime allotment, not a monthly
// grant (see audit_budget.py's own TIER_LIMITS/_LIFETIME_TIERS -- fixed
// there 2026-08-28 after it silently reset every calendar month). The
// two mode-picker cards below used to say "3 audits/month on Starter"
// unconditionally for every tier, which was simply false for Starter --
// these two computeds are the single place that phrasing is now decided,
// so the badge and the caption underneath it can't drift apart again.
const tierLimitLabel = computed(() => isPaidTier.value ? `${tierMonthlyLimit.value}/mo` : `${tierMonthlyLimit.value} total`);
const tierLimitCaption = computed(() => isPaidTier.value
  ? `${tierMonthlyLimit.value} audits/month on ${tierDisplayName.value}`
  : `${tierMonthlyLimit.value} audits total on ${tierDisplayName.value}`);
const canUseAutoCompetitors = computed(() => isPaidTier.value || isAdmin.value);

// Upgrade prompt computed props
const isStarterTier = computed(() => subscriptionTier.value === 'starter' || subscriptionTier.value === 'free');
const reportsExhausted = computed(() => isStarterTier.value && reportsRemaining.value <= 0);
const reportsLow = computed(() => isStarterTier.value && reportsRemaining.value > 0 && reportsRemaining.value <= 2);

// Audit-credit top-up (2026-08-28) -- mirrors ProspectingPage.vue's
// identical lead-credit-pack banners. Eligibility list is a verbatim
// mirror of audit_budget.py's own _BONUS_ELIGIBLE_TIERS -- Starter is
// a lifetime-capped trial tier (a different, not-yet-built offer) and
// Enterprise is already unlimited, so neither gets this banner.
const AUDIT_BONUS_ELIGIBLE_TIERS = ['professional', 'pro', 'growth', 'agency', 'scale', 'business'];
const isBonusEligibleTier = computed(() => AUDIT_BONUS_ELIGIBLE_TIERS.includes(subscriptionTier.value));

const auditsExhausted = computed(() => isBonusEligibleTier.value && reportsRemaining.value <= 0);
// Real threshold (reverted 2026-09-01, owner: "switch back now for the
// audit and lead pack to turn on when it reaches only 5 or less credits
// for each pack"). The temporary admin/Pro-tier-unconditional debug
// override used to click-test the Stripe checkout flow (2026-08-31,
// commits 09a0caa/b40f821) is fully removed -- this banner now only
// shows for a real eligible-tier account genuinely down to <=5 audits,
// matching the lead-credit-pack banner's own real threshold below.
const auditsLow = computed(() => isBonusEligibleTier.value && reportsRemaining.value > 0 && reportsRemaining.value <= 5);
// Provisional default -- kept in sync manually with
// core.config.settings.AUDIT_CREDIT_PACK_SIZE, same accepted pattern
// already documented for ProspectingPage.vue's creditPackSize.
const auditPackSize = ref(10);
// Kept in sync manually with the real Stripe Price object's amount
// (core.config.settings.STRIPE_PRICE_AUDIT_CREDITS_PACK) -- display-only,
// Stripe itself is the actual source of truth for what gets charged.
const auditPackPrice = ref(25);
// Real bug found 2026-08-31 (owner reported, from a real browser console
// log against ProspectingPage.vue's sibling buyCredits() -- same
// hand-copied pattern, same bug, here too): :disabled="buyingAudits"
// alone doesn't synchronously stop a rapid re-click before Vue repaints
// the DOM. Now backed by useCheckoutButton(), the one shared, tested
// implementation instead of a second hand-copied 20-line function -- see
// that composable's own docstring for the full story.
const { loading: buyingAudits, error: buyAuditsError, submit: _submitAuditsCheckout } = useCheckoutButton(router, route);
const buyAudits = () => _submitAuditsCheckout('/api/checkout/audits');
// Paid users: show monthly cap, enterprise shows 'Custom'
const reportsDisplay = computed(() => {
  if (isStarterTier.value) return reportsRemaining.value;
  const limit = tierMonthlyLimit.value;
  if (limit === 0) return 'Custom';
  return `${limit}/mo`;
});

const toast = (message, type = 'info') => {
  if (typeof window !== 'undefined' && window.$toast) window.$toast(message, { type });
};

const loadUrlHistory = () => {
  try {
    const stored = localStorage.getItem(URL_HISTORY_KEY);
    if (stored) urlHistory.value = JSON.parse(stored);
  } catch (err) { console.error('Failed to load URL history:', err); }
};

const saveUrlToHistory = (url) => {
  if (!url) return;
  const i = urlHistory.value.indexOf(url);
  if (i !== -1) urlHistory.value.splice(i, 1);
  urlHistory.value.unshift(url);
  if (urlHistory.value.length > 5) urlHistory.value = urlHistory.value.slice(0, 5);
  localStorage.setItem(URL_HISTORY_KEY, JSON.stringify(urlHistory.value));
};

const clearUrlHistory = () => {
  urlHistory.value = [];
  localStorage.removeItem(URL_HISTORY_KEY);
  toast('URL history cleared', 'info');
};

const truncateUrl = (url) => {
  if (!url) return '';
  const d = url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  return d.length > 24 ? d.substring(0, 21) + '…' : d;
};

const previewWebsite = async () => {
  if (!form.value.url) { toast('Please enter a URL first', 'warn'); return; }
  previewLoading.value = true;
  try {
    const response = await fetchWithCSRF(`/api/preview-website?url=${encodeURIComponent(form.value.url)}`);
    previewData.value = await response.json();
    // The check is DNS-only by design (see preview_website in
    // analysis_endpoints.py): the server makes no outbound HTTP request, so
    // whether the site is bot-protected is neither knowable nor relevant --
    // the analysis fetches through BrightData, which handles that.
    const d = previewData.value.domain || form.value.url;
    if (previewData.value.accessible) {
      toast(`${d} found — ready to analyse`, 'success');
    } else {
      toast(previewData.value.status || `Could not find ${d}`, 'error');
    }
  } catch (err) {
    toast('Failed to preview website', 'error');
    previewData.value = null;
  } finally {
    previewLoading.value = false;
  }
};

const suggestKeywords = async () => {
  if (!form.value.url) { toast('Enter a URL first', 'warn'); return; }
  suggestLoading.value = true;
  try {
    const response = await fetchWithCSRF('/api/suggest-keywords', {
      method: 'POST',
      body: JSON.stringify({ url: form.value.url }),
    });
    const data = await response.json();
    if (data.success && data.keywords?.length) {
      keywordSuggestions.value = data.keywords.slice(0, 10);
      toast(`Found ${keywordSuggestions.value.length} suggestions`, 'success');
    } else {
      toast('No keyword suggestions found', 'warn');
      keywordSuggestions.value = [];
    }
  } catch {
    toast('Failed to get keyword suggestions', 'error');
    keywordSuggestions.value = [];
  } finally {
    suggestLoading.value = false;
  }
};

const addKeyword = (keyword) => {
  form.value.keywords = form.value.keywords
    ? `${form.value.keywords}, ${keyword}`
    : keyword;
  toast(`Added: ${keyword}`, 'success');
};

const selectAdvancedMode = () => {
  if (!isPaidTier.value) {
    toast('Full JS rendering is on Pro and above — it uses more compute per scan.', 'warn');
    return;
  }
  form.value.mode = 'advanced';
  toast('Advanced mode selected — full JS rendering enabled', 'success');
};

// ---- Helper to clean domain input ----
// Analytics-safe hostname: GA4 params must not carry PII or full URLs with
// query strings, so only the host is ever sent (see analytics/events.js).
const domainForAnalytics = (input) => {
  const raw = String(input || '').trim();
  if (!raw) return null;
  try {
    return new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).hostname;
  } catch {
    return raw.slice(0, 120);
  }
};

const cleanDomainInput = (input) => {  return input
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./, '')
    .trim();
};

// ========== SUBMIT ANALYSIS ==========
const submitAnalysis = async () => {
  if (!form.value.url) { toast('Please enter a website URL', 'warn'); return; }
  loading.value = true;
  saveUrlToHistory(form.value.url);

  const keywords = form.value.keywords ? form.value.keywords.split(/[,\n]/).map(k => k.trim()).filter(Boolean) : [];

  // Clean competitors: strip http/https/www and trim
  const rawCompetitors = form.value.competitors ? form.value.competitors.split(/[\s,]+/).map(c => c.trim()).filter(Boolean) : [];
  const competitors = rawCompetitors.map(cleanDomainInput).filter(Boolean);

  const payload = {
    url: form.value.url,
    mode: form.value.mode,
    keywords,
    competitors,
    region: form.value.region,
    auto_competitors: form.value.auto_competitors
  };

  try {
    const response = await fetchWithCSRF('/api/public/analyze', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (data.success) {
      // GA4 audit_started -- the audit was accepted and is now running. The
      // completion side (audit_completed + generate_lead) fires from
      // ProcessingPage.vue's initiateRedirect().
      trackAuditStarted({ domain: domainForAnalytics(form.value.url), mode: form.value.mode });
      let message = `Analysis started with ${getRegionName(form.value.region)} search data! Redirecting...`;
      if (form.value.auto_competitors) {
        message = `Analysis started with auto-competitor discovery enabled! ${getRegionName(form.value.region)} search data. Redirecting...`;
      }
      toast(message, 'success');
      if (!data.analysis_id) {
        toast('Analysis submitted but no ID returned — check dashboard.', 'error');
        return;
      }
      localStorage.setItem('pending_analysis_id', data.analysis_id);
      setTimeout(() => {
        window.location.href = `/processing/${data.analysis_id}`;
      }, 800);
    } else if (data.upgrade_required) {
      // Show what the backend actually said. Two different gates set
      // upgrade_required -- the monthly audit quota AND the competitor-
      // comparison paid-feature check -- and they need entirely different
      // actions from the user. Collapsing both into "Upgrade required to
      // continue" made a blocked competitor toggle read as an exhausted
      // audit quota, while the badge correctly showed audits remaining.
      toast(data.error || 'Upgrade required to continue', 'warn');
      setTimeout(() => router.push('/pricing'), 2500);
    } else {
      toast(data.error || 'Analysis failed', 'error');
    }
  } catch (err) {
    const msg = String(err?.message || '');
    const cleanMsg = (msg.includes('is not valid JSON') || msg.includes('Unexpected token'))
      ? 'Server temporarily unavailable — please try again'
      : msg;
    toast('Network error: ' + cleanMsg, 'error');
  } finally {
    loading.value = false;
  }
};

// ========== UPDATED loadUserData ==========
const loadUserData = async () => {
  try {
    const response = await fetchWithCSRF('/api/auth/me');
    if (response.ok) {
      const data = await response.json();
      const user = data.user || {};
      reportsRemaining.value = user.reports_remaining || 0;
      totalAnalyses.value = user.total_analyses || 0;
      subscriptionTier.value = user.subscription_tier || 'starter';
      isAdmin.value = user.is_admin || false;
    }
  } catch (err) { console.error('Failed to load user data:', err); }
};

// ========== DEEP-LINK PARAMS (from the router) ==========
// The home-page hero deep-links here with ?url=<typed site> (and optional
// ?mode=). These come from the ROUTER, not window.location:
//   * Vue Router does not remount a component on a query-only navigation, so
//     an onMounted-only read silently kept the PREVIOUS url when the user
//     navigated home -> analyze -> analyze. Reading route.query reactively
//     (the watch below) makes the param authoritative on every change.
//   * window.location.search is not what the router's history abstraction
//     promises -- under any non-web history (the in-memory one core/prerender
//     and the test-suite use) it is empty, which made the funnel untestable
//     and silently dead outside a real browser.
const applyRouteParams = () => {
  const q = route.query || {};
  if (q.url) form.value.url = String(q.url);
  if (q.credits === 'success') {
    // Stripe's success_url for audit credit packs is
    // /analyze?credits=success&session_id=<cs_...> (src/api/stripe_endpoints.py).
    // auditPackPrice is this view's own display-only mirror of the Stripe
    // Price object, which is the only amount available client-side; Stripe
    // remains the source of truth for what was actually charged. Sending it
    // makes GA4 report the revenue instead of a bare $0 count. The session id
    // guards the event so a refresh cannot re-count the same pack.
    trackPurchase({
      item: 'audit_credits',
      tier: 'credits',
      value: auditPackPrice.value,
      transactionId: q.session_id,
    });
  }
  if (q.mode && ['quick', 'standard', 'advanced'].includes(String(q.mode))) {
    const mode = String(q.mode);
    if (mode === 'advanced' && !isPaidTier.value) {
      form.value.mode = 'standard';
    } else {
      form.value.mode = mode;
    }
  }
};

// ========== LIFECYCLE ==========
onMounted(async () => {
  syncCsrfTokenToMeta();
  loadUrlHistory();
  await loadRegions();
  await verifyPendingAnalysis();
  await loadUserData();

  applyRouteParams();
  
  const savedRegion = localStorage.getItem('preferred_region');
  if (savedRegion && availableRegions.value) {
    form.value.region = savedRegion;
  }
  
  // Tier-guard the restore, exactly as `mode` is guarded above. Without this
  // a Starter account that ever switched this on kept it on forever: the
  // backend then rejected EVERY analysis -- Quick Scan included -- with the
  // paid-feature gate, which looked like a broken audit quota.
  const savedAutoCompetitors = localStorage.getItem('preferred_auto_competitors');
  if (savedAutoCompetitors !== null) {
    form.value.auto_competitors = savedAutoCompetitors === 'true' && isPaidTier.value;
  }
  if (!isPaidTier.value && form.value.auto_competitors) {
    form.value.auto_competitors = false;
  }
});

// Re-apply ?url=/?mode= when the query changes WITHOUT a remount (e.g. a CTA
// navigating to /analyze?url=... while already on /analyze).
watch(() => route.query, applyRouteParams, { deep: true });

watch(() => form.value.region, (newRegion) => {
  localStorage.setItem('preferred_region', newRegion);
});

watch(() => form.value.auto_competitors, (newValue) => {
  localStorage.setItem('preferred_auto_competitors', String(newValue));
});
</script>

<style scoped>
.collapse-enter-active, .collapse-leave-active { transition: max-height 300ms cubic-bezier(.22,.61,.36,1), opacity 200ms ease; overflow: hidden; }
.collapse-enter-from, .collapse-leave-to { max-height: 0; opacity: 0; }
.collapse-enter-to, .collapse-leave-from { max-height: 800px; opacity: 1; }
.fade-enter-active, .fade-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-4px); }
</style>